import React, { useState, useEffect } from "react";
import { Modal, Steps, Spin, Form, Select, message, Tooltip } from "antd";
import { PlayCircleOutlined, CheckOutlined, PlusOutlined } from "@ant-design/icons";
import { FiFileText } from "react-icons/fi";
import { TbClipboardList } from "react-icons/tb";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

import drEasyApi from "../../../Api/Customapi";
import { getUserReport } from "../../../Api/getUserReport";
import { GeneralNotesPanel, LymphedemaAssessmentPanel, PhysioAssessmentPanel } from "./Panels";
import FinancialNotesTab from "./FinancialNotesTab";

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TYPE_INT = { General: 0, PhysioTherapy: 1, Lymphedema: 2 };
const REPORT_TYPE_STRING = { 0: "General", 1: "PhysioTherapy", 2: "Lymphedema" };

const TAB_VISIBILITY = {
  General: ["notes", "sessions"],
  PhysioTherapy: ["assessment", "sessions"],
  Lymphedema: ["lymphedema", "sessions"],
};

const ALL_TABS = [
  { id: "notes", label: "General Notes", icon: FiFileText },
  { id: "assessment", label: "PhysioTherapy", icon: TbClipboardList },
  { id: "lymphedema", label: "Lymphedema Assessment", icon: TbClipboardList },
];

const REPORT_TYPE_OPTIONS = [
  { value: 0, label: "General", description: "Standard patient notes and general documentation" },
  { value: 1, label: "PhysioTherapy", description: "Physiotherapy assessment, treatment plans and progress notes" },
  { value: 2, label: "Lymphedema", description: "Lymphedema-specific assessment and treatment tracking" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toReportTypeString = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return REPORT_TYPE_STRING[v] ?? null;
  if (typeof v === "string" && v in REPORT_TYPE_INT) return v;
  return null;
};

const toReportTypeInt = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  return REPORT_TYPE_INT[v] ?? 0;
};

const emptyAssessmentApiState = () => ({
  diagnosis: "", pastHistory: "", chiefComplain: "", jobAndOccupation: "",
  painScore: 0, painDistribution: "", lymphaticEdema: false,
  radioInvestigations: "", labNotes: false, labResults: "",
  precautions: "", ptAssessment: "", ptPlanOfTreatment: "",
  sessionsNum: 0, progressNotes: "", tttGoals: "", discharge: "",
  otherNotes: "", operations: "", presentHistory: "", skinAbnormalities: "",
  treatmentMLD: false, treatmentPCD: false, treatmentTECAR: false,
  treatmentBandaging: false, treatmentExercises: false, treatmentOther: "",
});

const buildAssessmentApiState = (report) => ({
  diagnosis: report.diagnosis || "",
  pastHistory: report.pastHistory || "",
  chiefComplain: report.chiefComplain || "",
  jobAndOccupation: report.jobAndOccupation || "",
  painScore: report.painScore ?? 0,
  painDistribution: report.painDistribution || "",
  lymphaticEdema: report.lymphaticEdema ?? false,
  radioInvestigations: report.radioInvestigations || "",
  labNotes: report.labNotes ?? false,
  labResults: report.labResults || "",
  precautions: report.precautions || "",
  ptAssessment: report.ptAssessment || "",
  ptPlanOfTreatment: report.ptPlanOfTreatment || "",
  sessionsNum: report.sessionsNum ?? 0,
  progressNotes: report.progressNotes || "",
  tttGoals: report.tttGoals || "",
  discharge: report.discharge || "",
  otherNotes: report.otherNotes || "",
  operations: report.operations || "",
  presentHistory: report.presentHistory || "",
  skinAbnormalities: report.skinAbnormalities || "",
  treatmentMLD: report.treatmentMLD ?? false,
  treatmentPCD: report.treatmentPCD ?? false,
  treatmentTECAR: report.treatmentTECAR ?? false,
  treatmentBandaging: report.treatmentBandaging ?? false,
  treatmentExercises: report.treatmentExercises ?? false,
  treatmentOther: report.treatmentOther || "",
});

const hydrateFromReport = (report, { setFreeFormNotes, setSavedSections, setAssessmentApiState }) => {
  setFreeFormNotes(report.body || "");
  setSavedSections(
    (report.reportSections || []).map((s) => ({
      sectionId: s.id,
      title: s.title || "",
      content: s.body || "",
      photoIDs: s.photoIds || [],
      createdAt: s.createdAt,
    }))
  );
  setAssessmentApiState(buildAssessmentApiState(report));
};

// ─── Create Report Modal ──────────────────────────────────────────────────────

function CreateReportModal({ open, onClose, canClose, onConfirm, creating }) {
  const [selected, setSelected] = useState(null);

  return (
    <Modal
      open={open} onCancel={canClose ? onClose : undefined}
      footer={null} closable={canClose} maskClosable={canClose}
      width={500} centered
      title={<div className="text-lg font-bold text-gray-800 pb-1">Create Patient Report</div>}
    >
      <p className="text-gray-500 mb-5 text-sm">
        Select the report type for this patient.
        {!canClose && " A report is required to unlock assessment and notes tabs."}
      </p>
      <div className="flex flex-col gap-3 mb-5">
        {REPORT_TYPE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setSelected(opt.value)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected === opt.value
              ? "border-primary bg-primary/5"
              : "border-gray-200 hover:border-primary/40 hover:bg-gray-50"
              }`}
          >
            <div className="flex items-center gap-3">
              <div>
                <p className={`font-semibold text-sm ${selected === opt.value ? "text-primary" : "text-gray-800"}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
              </div>
              {selected === opt.value && <span className="ml-auto text-primary text-lg">✓</span>}
            </div>
          </button>
        ))}
      </div>
      <div className="flex gap-3 justify-end">
        {canClose && (
          <button onClick={onClose}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm">
            Cancel
          </button>
        )}
        <button
          onClick={() => { if (selected !== null) onConfirm(selected); }}
          disabled={selected === null || creating}
          className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${selected === null || creating
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-primary text-white hover:bg-primary/90"
            }`}
        >
          {creating ? "Creating…" : "Create Report"}
        </button>
      </div>
    </Modal>
  );
}

// ─── MAIN: StartSessionModal ──────────────────────────────────────────────────

export default function StartSessionModal({
  open,
  session,
  onClose,
  onSessionComplete,
  updateSessionMutation,
  isMobile = false,
  isCompleted, note
}) {
  // ── Report state ──
  const [loading, setLoading] = useState(false);
  const [filteredReports, setFilteredReports] = useState([]);
  const [currentReportIndex, setCurrentReportIndex] = useState(0);
  const [reportFound, setReportFound] = useState(false);
  const [reportType, setReportType] = useState("General");

  // ── Tab & UI state ──
  const [activeTab, setActiveTab] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingReport, setCreatingReport] = useState(false);

  // ── Report content state ──
  const [freeFormNotes, setFreeFormNotes] = useState("");
  const [savedSections, setSavedSections] = useState([]);
  const [assessmentApiState, setAssessmentApiState] = useState(emptyAssessmentApiState());

  // ── Step & completion state ──
  // Steps: 0 = Report & Notes, 1 = Financial Report, 2 = Complete Session
  const [currentStep, setCurrentStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [completionForm] = Form.useForm();
  const [assessmentForm] = Form.useForm();

  // ── Financial report state ──
  const [orderNotes, setOrderNotes] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [existingOrderReport, setExistingOrderReport] = useState(null);
  const [loadingOrderReport, setLoadingOrderReport] = useState(false);

  // ─── Derived ───────────────────────────────────────────────────────────────
  const currentReport = filteredReports[currentReportIndex] ?? null;
  const reportId = currentReport?.id ?? null;
  const physioTherapyData = currentReport?.physioTherapyData ?? null;
  const lymphedemaData = currentReport?.lymphedemaData ?? null;

  // Build a patientData-compatible shape for FinancialNotesTab
  const financialPatientData = session
    ? { patientSessionId: session.sessionId, centerID: session.centerID }
    : null;

  const [patientInfo, setPatientInfo] = useState(null)
  async function fetchPatient() {
    try {

      const res = await drEasyApi.get("/api/Centers/GetPatientInfo", { params: { userId: session.patientId } })
      setPatientInfo(res.data)
    } catch (err) {
      message.error(err.response.data.errors[0])
    }



  }


  // ─── Set active tab after report loads ────────────────────────────────────
  useEffect(() => {
    if (!reportFound) { setActiveTab(null); return; }
    if (!reportType) return;
    const visibleIds = TAB_VISIBILITY[reportType] ?? [];
    setActiveTab(visibleIds[0] ?? null);
  }, [reportType, reportFound]);

  // ─── State helpers ────────────────────────────────────────────────────────
  const noteText = Form.useWatch("noteText", completionForm);
  const [noteTextValue, setNoteTextValue] = useState("");
  const resetState = () => {
    setLoading(false);
    setFilteredReports([]);
    setCurrentReportIndex(0);
    setReportFound(false);
    setReportType(null);
    setActiveTab(null);
    setFreeFormNotes("");
    setSavedSections([]);
    setAssessmentApiState(emptyAssessmentApiState());
    setCurrentStep(0);
    setCompleting(false);
    setOrderNotes("");
    setExistingOrderReport(null);
    setLoadingOrderReport(false);
    completionForm.resetFields();
    setNoteTextValue(session?.noteText || ""); // ← simple, reliable
    assessmentForm.resetFields();
  };
  useEffect(() => {
    if (!open || !session) return;
    resetState();
    completionForm.setFieldsValue({ noteText: session.noteText || null }); // ← add after resetState
    loadReport(session.patientId, session.defaultReportType ?? reportType);
    if (session.sessionId) fetchOrderReport(session.sessionId);
    fetchPatient();
  }, [open, session?.sessionId]);
  const loadReport = async (userId, reportTypeValue) => {
    setLoading(true);
    try {
      const data = await getUserReport(userId);
      processReportData(data, reportTypeValue);
    } catch {
      setReportFound(false);
      setFilteredReports([]);
    } finally {
      setLoading(false);
    }
  };

  const processReportData = (data, reportTypeValue) => {
    const typeStr = toReportTypeString(reportTypeValue);
    const reports = Array.isArray(data) ? data : [data];
    const matched = typeStr
      ? reports.filter((r) => toReportTypeString(r.reportType) === typeStr)
      : reports;

    if (matched.length > 0) {
      setFilteredReports(matched);
      setCurrentReportIndex(0);
      setReportFound(true);
      const first = matched[0];
      const resolvedType =
        toReportTypeString(first.defaultReportType) ?? typeStr ?? toReportTypeString(first.reportType);
      setReportType(resolvedType);
      hydrateFromReport(first, { setFreeFormNotes, setSavedSections, setAssessmentApiState });
    } else {
      setFilteredReports([]);
      setReportFound(false);
      if (typeStr) setReportType(typeStr);
    }
  };

  const fetchAndRestoreReport = async () => {
    if (!session?.patientId) return;
    try {
      const data = await getUserReport(session.patientId);
      const typeStr = reportType;
      const reports = Array.isArray(data) ? data : [data];
      const matched = typeStr
        ? reports.filter((r) => toReportTypeString(r.reportType) === typeStr)
        : reports;

      if (matched.length > 0) {
        setFilteredReports(matched);
        setReportFound(true);
        const restoredIdx = reportId
          ? Math.max(matched.findIndex((r) => r.id === reportId), 0)
          : currentReportIndex;
        setCurrentReportIndex(restoredIdx);
        hydrateFromReport(matched[restoredIdx], { setFreeFormNotes, setSavedSections, setAssessmentApiState });
      }
    } catch { }
  };

  const fetchOrderReport = async (patientSessionId) => {
    setLoadingOrderReport(true);
    try {
      const res = await drEasyApi.get(`/api/Reports/OrderReport`, { params: { patientSessionId: session.sessionId } });
      if (res.data) {
        setExistingOrderReport(res.data);
        setOrderNotes(res.data.notes || "");
      }
    } catch {
      setExistingOrderReport(null);
    } finally {
      setLoadingOrderReport(false);
    }
  };

  const handleReportTypeChange = async (value) => {
    setReportType(value);
    setReportFound(false);
    setFilteredReports([]);
    setCurrentReportIndex(0);
    if (session?.patientId) await loadReport(session.patientId, value);
  };

  const handleReportIndexChange = (newIdx) => {
    if (newIdx < 0 || newIdx >= filteredReports.length) return;
    setCurrentReportIndex(newIdx);
    hydrateFromReport(filteredReports[newIdx], { setFreeFormNotes, setSavedSections, setAssessmentApiState });
  };

  const handleCreateReport = async (selectedType) => {
    setCreatingReport(true);
    try {
      await drEasyApi.post("/api/Reports/CraeteReport", {
        userId: session.patientId,
        reportType: selectedType,
      });
      message.success("Report created successfully");
      setShowCreateModal(false);
      await loadReport(session.patientId, selectedType);
    } catch {
      message.error("Failed to create report");
    } finally {
      setCreatingReport(false);
    }
  };

  const handleSaveReport = async (extra = {}) => {
    await drEasyApi.put("/api/Reports/EditReport", {
      reportId,
      body: freeFormNotes,
      photoIDs: [],
      ...assessmentApiState,
      ...extra,
    });
  };

  const handleCompleteSession = async () => {
    try {
      const values = await completionForm.validateFields();
      setCompleting(true);
      if (isCompleted) {
        updateSessionMutation.mutate(
          {
            sessionId: session.sessionId,
            newStatus: 2,
            noteText: values.noteText,
            additionalNote: values.additionalNote,
          },
          {
            onSuccess: () => {
              message.success("Session completed successfully");
              onSessionComplete?.();
              onClose();
            },
            onError: () => {
              message.error("Failed to complete session");
              setCompleting(false);
            },
          }
        );
      } else {
        await drEasyApi.put("/api/PatientSessions/EditSessionNote", {
          sessionId: session.sessionId,
          noteText: values.noteText,
        });
        onSessionComplete?.();
        onClose();
      }
    } catch {
      setCompleting(false);
    }
  };

  // ─── Tabs ──────────────────────────────────────────────────────────────────
  const visibleTabIds = reportFound ? (TAB_VISIBILITY[reportType] ?? []) : [];
  const tabs = ALL_TABS.filter((t) => visibleTabIds.includes(t.id));

  // ─── Footer ────────────────────────────────────────────────────────────────
  const modalFooter = (
    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
      {currentStep === 0 && (
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => setCurrentStep(1)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-lg font-semibold text-sm transition-colors shadow"
          >
            Next →
          </button>
        </>
      )}

      {currentStep === 1 && (
        <>
          <button
            onClick={() => setCurrentStep(0)}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={() => setCurrentStep(2)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-lg font-semibold text-sm transition-colors shadow"
          >
            Next →
          </button>
        </>
      )}
      {/* {currentStep === 1 && !isCompleted && (
        <>
          <button
            onClick={() => setCurrentStep(0)}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={() => onClose()}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-5 py-2 rounded-lg font-semibold text-sm transition-colors shadow"
          >
            Done
          </button>
        </>
      )} */}

      {currentStep === 2 && (
        <>
          <button
            onClick={() => setCurrentStep(1)}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={handleCompleteSession}
            disabled={completing || updateSessionMutation?.isPending}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-semibold text-sm transition-colors shadow disabled:opacity-50"
          >
            <CheckOutlined />
            {completing || updateSessionMutation?.isPending ? "Completing..." : isCompleted ? "Complete Session" : "Done"}
          </button>
        </>
      )}
    </div>
  );
  console.log(session, "sdsd");

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={
          <div className="flex items-center gap-3 py-1">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <PlayCircleOutlined className="text-green-600 text-base" />
            </div>
            <div>
              <div className="font-bold text-gray-800 text-base leading-tight">Start Session</div>
              {session && (
                <div className="text-xs text-gray-500 font-normal">
                  {session.patientName} · {dayjs(session.sessionDate).format("MMM D, YYYY")}
                </div>
              )}
            </div>
          </div>
        }
        width={isMobile ? "95vw" : 980}
        centered={isMobile}
        footer={null}
        styles={{ body: { padding: 0, maxHeight: "82vh", overflowY: "auto", } }}
        style={{ top: isMobile ? undefined : 20 }}
      >
        <div className="px-0 md:px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spin size="large" tip="Loading patient report..." />
            </div>
          ) : (
            <>
              {/* Step indicator */}
              <Steps
                current={currentStep}
                size="small"
                className="mb-5"
                items={[
                  { title: "Report & Notes" },
                  { title: "Financial Report" },
                  { title: "Complete Session" },
                ]}
              />

              {/* ── STEP 0: Report & Notes ─────────────────────────────────── */}
              {currentStep === 0 && (
                <>
                  {/* No report banner */}
                  {!reportFound && (
                    <div className="mb-5 flex flex-col items-center justify-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 py-8 px-4 text-center">
                      <p className="text-base font-bold text-gray-800">
                        No {reportType || "Report"} found for this patient
                      </p>
                      <p className="text-sm text-gray-500">
                        Create a report to enable notes and assessment tabs.
                      </p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 font-semibold text-white hover:bg-primary/90 transition-colors text-sm"
                      >
                        <PlusOutlined /> Create Report
                      </button>
                    </div>
                  )}

                  {/* Tab bar */}
                  <div className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden">
                    <div className="flex items-center justify-between border-b border-gray-200 px-4 flex-wrap gap-y-1">
                      {/* Tabs */}
                      <div className="flex flex-wrap">
                        {tabs.map(({ id, label, icon: Icon }) => (
                          <button
                            key={id}
                            onClick={() => setActiveTab(id)}
                            className={`flex items-center gap-1.5 px-4 py-3 font-semibold text-sm transition-all relative ${activeTab === id ? "text-primary" : "text-gray-500 hover:text-gray-700"
                              }`}
                          >
                            <Icon size={16} />
                            {label}
                            {activeTab === id && (
                              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                            )}
                          </button>
                        ))}
                        {tabs.length === 0 && (
                          <span className="px-4 py-3 text-sm text-gray-400 italic">
                            No tabs available — create a report first
                          </span>
                        )}
                      </div>

                      {/* Right controls */}
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-xs text-gray-500 whitespace-nowrap">Report type:</span>
                        <Select
                          style={{ width: 160 }}
                          value={reportType}
                          onChange={handleReportTypeChange}
                          placeholder="Select type"
                          size="small"
                        >
                          <Select.Option value="General">General</Select.Option>
                          <Select.Option value="PhysioTherapy">PhysioTherapy</Select.Option>
                          <Select.Option value="Lymphedema">Lymphedema</Select.Option>
                        </Select>
                        <Tooltip title="Create Report">
                          <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-1 px-3 py-1 rounded-lg border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-white transition-colors text-xs"
                          >
                            <PlusOutlined />
                            <span className="hidden md:block">
                              {reportFound ? "New Report" : "Create"}
                            </span>
                          </button>
                        </Tooltip>
                      </div>
                    </div>

                    {/* Tab content */}
                    <div className=" p-2 md:p-4 min-h-[300px]">
                      {activeTab === "notes" && reportFound && (
                        <GeneralNotesPanel
                          reportId={reportId}
                          freeFormNotes={freeFormNotes} setFreeFormNotes={setFreeFormNotes}
                          savedSections={savedSections} setSavedSections={setSavedSections}
                          onSaveNotes={handleSaveReport}
                          fetchReport={fetchAndRestoreReport}
                          reportCount={filteredReports.length}
                          currentReportIndex={currentReportIndex}
                          onReportIndexChange={handleReportIndexChange}
                        />
                      )}

                      {activeTab === "assessment" && reportFound && (
                        <PhysioAssessmentPanel
                          assessmentForm={assessmentForm}
                          assessmentApiState={assessmentApiState}
                          setAssessmentApiState={setAssessmentApiState}
                          onSave={handleSaveReport}
                          fetchReport={fetchAndRestoreReport}
                          physioTherapyData={physioTherapyData}
                          patientInfo={patientInfo}
                          reportCount={filteredReports.length}
                          currentReportIndex={currentReportIndex}
                          onReportIndexChange={handleReportIndexChange}
                          session={session}
                        />
                      )}

                      {activeTab === "lymphedema" && reportFound && (
                        <LymphedemaAssessmentPanel
                          assessmentForm={assessmentForm}
                          assessmentApiState={assessmentApiState}
                          setAssessmentApiState={setAssessmentApiState}
                          onSave={handleSaveReport}
                          fetchReport={fetchAndRestoreReport}
                          lymphedemaData={lymphedemaData}
                          reportId={reportId}
                          patientInfo={patientInfo}
                          reportCount={filteredReports.length}
                          currentReportIndex={currentReportIndex}
                          onReportIndexChange={handleReportIndexChange}
                          session={session}
                        />
                      )}

                      {!activeTab && reportFound && (
                        <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                          Select a tab above to get started
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── STEP 1: Financial Report ───────────────────────────────── */}
              {currentStep === 1 && (
                <div className="mb-4">
                  <FinancialNotesTab
                    patientData={financialPatientData}
                    orderNotes={orderNotes}
                    setOrderNotes={setOrderNotes}
                    submittingOrder={submittingOrder}
                    setSubmittingOrder={setSubmittingOrder}
                    existingOrderReport={existingOrderReport}
                    loadingOrderReport={loadingOrderReport}
                    fetchOrderReport={() => fetchOrderReport(session.sessionId)}
                    session={session}
                  />
                </div>
              )}

              {/* ── STEP 2: Complete Session ───────────────────────────────── */}
              {currentStep === 2 && (
                <div className="border-2 border-green-200 rounded-xl p-5 bg-green-50 mb-4">
                  <h3 className="font-bold text-gray-800 text-base mb-4 flex items-center gap-2">
                    <CheckOutlined className="text-green-500" /> Complete Session
                  </h3>
                  <div className="mb-4 p-3 bg-white rounded-lg border border-green-100 text-sm space-y-1">
                    <p><strong>Patient:</strong>    {session?.patientName}</p>
                    <p><strong>Date:</strong>       {session?.sessionDate ? dayjs(session.sessionDate).format("MMM D, YYYY") : "N/A"}</p>
                    <p><strong>Specialist:</strong> {session?.specialistName}</p>
                  </div>
                  <Form form={completionForm} layout="vertical">
                    <Form.Item
                      name="noteText"
                      label="Session Notes"
                      // rules={[
                      //   { required: true, message: "Please add session notes" },
                      //   { min: 10, message: "Notes must be at least 10 characters" },
                      // ]}
                      initialValue={note || null}
                    >
                      <TextArea
                        rows={4}
                        value={noteTextValue}
                        onChange={(e) => setNoteTextValue(e.target.value)}
                        placeholder="Enter session notes, diagnosis, treatment plan..."
                        className="rounded-lg"
                      />
                    </Form.Item>
                    {/* {!isCompleted &&
                      <Form.Item name="additionalNote" label="Additional Notes (Optional)">
                        <TextArea
                          rows={3}
                          placeholder="Any additional observations or recommendations..."
                          className="rounded-lg"
                        />
                      </Form.Item>} */}
                  </Form>
                </div>
              )}

              {/* Footer */}
              {modalFooter}
            </>
          )}
        </div>
      </Modal>

      {/* Create report sub-modal */}
      <CreateReportModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        canClose={true}
        onConfirm={handleCreateReport}
        creating={creatingReport}
      />
    </>
  );
}